/* eslint-disable react-refresh/only-export-components -- Súbor exportuje hook aj provider kvôli zdieľaniu logiky autentifikácie. */
import { useState, useEffect, createContext, useContext } from 'react';
import { supabase } from '../services/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
      setLoading(false);
    };

    getSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email, password) => {
    // Validácia Supabase konfigurácie pred prihlásením
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    if (!supabaseUrl || !supabaseAnonKey) {
      const configError = new Error(
        'Supabase nie je správne nakonfigurované. Kontaktujte administrátora.'
      );
      configError.name = 'ConfigurationError';
      throw configError;
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      // Lepšia error správa pre rôzne typy chýb
      if (error.message?.includes('Invalid login credentials')) {
        throw new Error('Nesprávne prihlasovacie údaje');
      } else if (error.message?.includes('Email not confirmed')) {
        throw new Error('Email nie je potvrdený. Skontrolujte svoju schránku.');
      } else if (error.status === 400) {
        throw new Error('Chyba prihlásenia. Skontrolujte, či sú Supabase credentials správne nastavené.');
      }
      throw error;
    }
    
    return data;
  };

  const logout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const value = {
    user,
    login,
    logout,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};